import { find } from 'lodash';
import { action, autorun, computed, observable, reaction } from 'mobx';
import { addLocaleData, MessageValue } from 'react-intl';
import { ResizeEntry } from '@blueprintjs/core';

import { IAppRoute } from './RouterStore';
import { IImagesBackendEnum } from '../../modules/commons/components/imageCropper/types';
import { ActionType } from '../types/ActionType';
import { IAppOverlayProps } from '../types/IAppOverlay';
import { ResolutionSizeType } from '../types/ResolutionSizeType';
import { ResolutionType } from '../types/ResolutionType';
import { ApplicationFeatureListType, UserGroupType } from '../types/UserGroup';
import { getCommodityById } from '../utils/utilCommodity';
import { getUrlLogo } from '../utils/utilCompany';
import { isDesktop, toTitleCase } from '../utils/utils';
import en from 'react-intl/locale-data/en';
import es from 'react-intl/locale-data/es';
import userService from '../../modules/commons/services/UserService';
import enMessage from '../../translations/en.json';
import esMessage from '../../translations/es.json';
import authService from '../services/AuthServices';
import genericService from '../services/GenericService';
import messagingService from '../../modules/dispatch/components/messaging/services/MessagingService';

export interface ITheme {
  primary: string;
  primary_70?: string;
  primary_50?: string;
  primary_20?: string;
  secondary: string;
  secondary_70?: string;
  secondary_50?: string;
  secondary_20?: string;
  accent_blue: string;
  accent_blue_70: string;
  accent_gray: string;
  accent_red: string;
  accent_yellow: string;
  accent_green: string;
  dark_grey: string;
  light_grey: string;
  sidebar: string;
  sidebar_text: string;
  headers: string;
  headers_text: string;
  background: string;
  light_green: string;
  light_orange: string;
  accent_grey: string;
}

export class AppStore {
  @observable isLoading = false;
  @observable selectedCommodity = 1;
  @observable userGroup = null as UserGroupType | null;
  @observable trainingVideoUrl = '' as string | string[];
  @observable activeTab = 'tabDashboard';
  @observable activeTabItem = 'tabDashboard';
  @observable isUserGroupReady = false;
  @observable isOpenProcess = false;
  @observable theme = themes['light'] as ITheme;
  @observable lang = 'en';
  @observable resolution = 'xs' as ResolutionType;
  @observable _resolutionSize = { width: 0, height: 0 } as ResolutionSizeType;
  @observable formState = 'NONE' as ActionType;
  @observable filterContext = {} as any;
  @observable terminalSelectFilters = [] as any[];
  @observable appOverlay = null as IAppOverlayProps | null;
  @observable user: any;
  @observable momentLocale = 'en-us';
  @observable eldtabPermission: boolean = false;
  @observable eldType: string = '';
  @observable _intl: any;
  @observable parsedCompanyLogoUrl: string = '';
  @observable parsedTabletLogoUrl: string = '';
  @observable isDesktopDevice: boolean = isDesktop();
  @observable unReadMessageCount: number = 0;

  constructor() {
    this.setSelectedCommodityOnLogin();
    reaction(
      () => this.user,
      () => {
        this.eldType = authService.getEldType();
        this.eldtabPermission = authService.getEldtabPermission();
        this.terminalSelectFilters = [authService.getDefaultUserTerminal()];
        appStore.getUnreadMessageCount();
      }
    );
  }

  @action setAppOverlay(value?: IAppOverlayProps | null) {
    this.appOverlay = value || null;
    if (value) {
      setTimeout(() => {
        this.setAppOverlayShowButton();
      }, 7000);
    }
  }

  @action setIntl = (intl: any) => {
    if (!this._intl) {
      this._intl = intl;
    }
  };

  intl = (id: string, defaultMessage = '', values?: { [key: string]: MessageValue }) =>
    this._intl({ id, defaultMessage }, values);

  @action setAppOverlayShowButton() {
    if (this.appOverlay) {
      this.appOverlay.show = true;
    }
  }

  @action setSelectedCommodityOnLogin = () => {
    this.setSelectedCommodity(authService.getActiveCommodity() || 1, !!authService.getActiveCommodity());
  };

  @action setLoading(value: boolean) {
    this.isLoading = value;
  }

  @action setIsUserGroupReady(value: boolean) {
    this.isUserGroupReady = value;
  }

  @action setUser(value: any) {
    this.user = value;
  }

  @action setDesktopDevice(value: boolean) {
    this.isDesktopDevice = value;
  }

  @action setMomentLocate = (locate: string) => {
    this.momentLocale = locate;
  };

  @action setIsOpenProcess(value: boolean) {
    this.isOpenProcess = value;
  }

  @action setResolutionSize(value: ResolutionSizeType) {
    if (value.width < 576) {
      this.resolution = 'xs';
    } else if (value.width >= 576 && value.width < 769) {
      this.resolution = 'sm';
    } else if (value.width > 770 && value.width < 992) {
      this.resolution = 'md';
    } else {
      this.resolution = 'lg';
    }
    this.setDesktopDevice(isDesktop());
    this._resolutionSize = value;
  }

  @computed get isLogged() {
    return authService.isLoggedIn();
  }

  @computed get isOpenOverlay(): boolean {
    return !!this.appOverlay?.isOpen;
  }

  @computed get isShowButtonOverlay(): boolean {
    return !!this.appOverlay?.show;
  }

  @computed get isCallCenterUser(): boolean {
    return this.userGroup?.name === 'CallCenter';
  }

  @computed get overlayText(): string {
    return this.appOverlay?.text || '';
  }

  @computed get overlayWidth(): number | undefined {
    return this.appOverlay?.width;
  }

  @computed get unReadCount(): number {
    return this.unReadMessageCount;
  }

  @computed get messages() {
    const lang = this.lang;
    switch (lang) {
      case 'en':
        addLocaleData(en);
        return enMessage;
      case 'es':
      default:
        addLocaleData(es);
        return esMessage;
    }
  }

  @computed get resolutionTypeWidth() {
    return this._resolutionSize?.width;
  }

  @computed get resolutionTypeHeight() {
    return this._resolutionSize?.height;
  }

  @computed get isLgResolution() {
    return this.resolution === 'lg';
  }

  @computed get isMobileMenu() {
    return ['xs', 'sm'].includes(this.resolution) || !this.isDesktopDevice;
  }

  @computed get isMobile() {
    return /Mobi|Android/i.test(navigator.userAgent);
  }

  @action resetUser() {
    authService.clearToken();
  }

  @action setEldtabPermission(value?: boolean) {
    this.eldtabPermission = value !== undefined ? value : authService.getEldtabPermission();
  }

  @computed get hasEldtabPermission() {
    return this.eldtabPermission;
  }

  @computed get isNewPortalCreation(): boolean {
    return this.user?.org?.name === 'welltraxportal';
  }

  @computed get isTicketImportLockBarrels(): boolean {
    return this.user?.isTicketImportLockBarrels;
  }

  @action setEldType(value?: string) {
    this.eldType = value !== undefined ? value : authService.getEldType();
  }

  @computed get getEldType() {
    return this.eldType;
  }

  handleResize = (entries: ResizeEntry[]) => {
    entries.forEach(value => {
      const { width, height } = value.contentRect;
      this.setResolutionSize({ width, height });
    });
  };

  public setLayoutOnWindowsResize(entityStore: any, propertyName: string = 'setDetailsLocked') {
    const setDetailsLockedMethod = entityStore?.[propertyName];
    autorun(() => {
      if (setDetailsLockedMethod) {
        if (this.resolution !== 'lg') {
          setDetailsLockedMethod(false);
        } else {
          if (!entityStore.isUnpinned) {
            setTimeout(() => {
              setDetailsLockedMethod(true);
            }, 0);
          }
        }
      }
    });
  }

  @action setTheme(value: ITheme) {
    this.theme = value;
  }

  @action setLang(value: string) {
    this.lang = value;
  }

  @action setSelectedCommodity = (id: number, noUpdateLS?: boolean) => {
    this.selectedCommodity = id;
    this.filterContext = { ...this.filterContext, commodityId: id };
    authService.setCommodity(id, noUpdateLS);
  };

  @action setTerminalSelectedFilter = (selected: any[]) => {
    this.terminalSelectFilters = selected;
  };

  @computed get selectedCommodityName() {
    return toTitleCase(getCommodityById(this.selectedCommodity)?.description || '');
  }

  @computed get selectedCommodityObject() {
    return getCommodityById(this.selectedCommodity);
  }

  @action setTrainingVideoUrl = (url: string | string[]) => {
    this.trainingVideoUrl = url;
  };

  /**
   * @deprecated use isWidthResolutionOrMinor or isHeightResolutionOrMinor, depend
   * @param value : resolution number
   * @description Return true if resolution is equal or minor than `value`
   */
  isResolutionOrMinor(value: number) {
    return this.resolutionTypeWidth <= value;
  }

  isWidthResolutionOrMinor(value: number) {
    return this.resolutionTypeWidth <= value || false;
  }

  isHeightResolutionOrMinor(value: number) {
    return this.resolutionTypeHeight <= value || false;
  }

  @action setUserGroup(value: UserGroupType) {
    this.userGroup = value;
  }

  @action setActiveTab(value: string) {
    this.activeTab = value;
    this.setActiveTabItemByKeyVisible(value);
  }

  @action setActiveTabItem(value: string) {
    this.activeTabItem = value;
  }

  @action setFilterContext(value: any) {
    this.filterContext = value;
  }

  setActiveTabItemByKeyVisible(value: string) {
    const tab = this.getNextActiveTabKeyPermission(value);
    if (tab) {
      this.setActiveTabItem(tab);
    }
  }

  getNextActiveTabKeyPermission = (parentTab: string): string => {
    if (this.userGroup && parentTab) {
      const tab: ApplicationFeatureListType | undefined = find(
        this.userGroup.applicationFeatureList,
        (val: ApplicationFeatureListType) => {
          return val.internalName.includes(parentTab) && val.visible;
        }
      );
      if (tab) {
        return tab.internalName;
      }
    }
    return '';
  };

  @action setFormState(value: ActionType) {
    this.formState = value;
  }

  public storeUserCredentials(credentials: any) {
    authService.storeUserCredentials(credentials);
    this.setSelectedCommodityOnLogin();
    this.setUser(credentials);
  }

  public getUseCalculationPreferences() {
    const useCalculationData: any = localStorage.getItem('useCalculation');
    return (
      JSON.parse(useCalculationData) ?? {
        useCalculation: 'NO',
        lastDropReturn: 'NO'
      }
    ); //default values if first time
  }

  public storeUseCalculationPreferences(useCalculationData: any) {
    localStorage.setItem('useCalculation', JSON.stringify(useCalculationData));
  }
  public storeUserData(userData: any) {
    localStorage.setItem('userdata', JSON.stringify(userData));
  }

  public logout = async () => {
    await userService.logout();
    authService.clearToken();
    this.setFormState('NONE');
  };

  getUserGroups(): Promise<boolean> {
    if (this.user && this.user.hasOwnProperty('id')) {
      return genericService
        .getById('user/user_group', this.user.id)
        .then((response: any) => {
          if (response && response.data) {
            this.setUserGroup(response.data);
            this.setIsUserGroupReady(true);
            return Promise.resolve(true);
          }
          return Promise.resolve(true);
        })
        .catch(() => {
          return Promise.resolve(true);
        });
    }
    return Promise.resolve(false);
  }

  getUserGroupsPermissionByKey(key: string) {
    if (this.userGroup && key && this.userGroup.hasOwnProperty('applicationFeatureList')) {
      const list: ApplicationFeatureListType[] = this.userGroup.applicationFeatureList;
      const item = find(list, (value: ApplicationFeatureListType) => {
        return value.internalName.toString() === key.toString();
      });
      return item ?? null;
    }
    return null;
  }

  getUserGroupsFromLocalStorageByKey(key: string) {
    const userData: any = localStorage.getItem('userdata');
    const { group }: any = JSON.parse(userData);
    if (group && key && group.hasOwnProperty('applicationFeatureList')) {
      const item = group.applicationFeatureList?.find(
        (elem: ApplicationFeatureListType) => elem.internalName.toString() === key.toString()
      );
      return item ?? null;
    }
    return null;
  }

  getUserGroupsPermissionByFirstVisibleTab(routes: any): string {
    if (!!routes?.length) {
      for (const { children, completePath } of routes) {
        if (!children?.length && !!completePath) {
          return completePath;
        } else if (!!children?.length) {
          const internalList = children.filter(({ permission }: Partial<IAppRoute>) => {
            const permissions: any = appStore.getUserGroupsPermissionByKey(permission ?? '');
            return !!permissions?.visible;
          });

          if (internalList?.length) return this.getUserGroupsPermissionByFirstVisibleTab(internalList);
        }
      }
    }
    return '';
  }

  getTimeZone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  @action setParsedCompanyLogoUrl = (parsedCompanyLogoUrl: string) => {
    this.parsedCompanyLogoUrl = parsedCompanyLogoUrl;
  };

  @action setParsedTabletLogoUrl = (parsedTabletLogoUrl: string) => {
    this.parsedTabletLogoUrl = parsedTabletLogoUrl;
  };

  @action setUnReadMessageCount = (count: number) => {
    this.unReadMessageCount = count;
  };

  handleUpdateCompanyLogoToDisplay = async (documentList: any[]) => {
    if (getUrlLogo(documentList, IImagesBackendEnum.COMPANY_LOGO_130_100)) {
      await fetch(getUrlLogo(documentList, IImagesBackendEnum.COMPANY_LOGO_130_100), {
        method: 'get',
        credentials: 'include',
        headers: {
          Accept: '*/*',
          Source: 'portal'
        }
      })
        .then(response => response.blob())
        .then(response => {
          this.setParsedCompanyLogoUrl(URL.createObjectURL(response) || '');
        })
        .catch(() => {
          this.setParsedCompanyLogoUrl('');
        });
    } else {
      this.setParsedCompanyLogoUrl('');
    }
    if (getUrlLogo(documentList, IImagesBackendEnum.COMPANY_LOGO_300_100)) {
      await fetch(getUrlLogo(documentList, IImagesBackendEnum.COMPANY_LOGO_300_100), {
        method: 'get',
        credentials: 'include',
        headers: {
          Accept: '*/*',
          Source: 'portal'
        }
      })
        .then(response => response.blob())
        .then(response => {
          this.setParsedTabletLogoUrl(URL.createObjectURL(response) || '');
        })
        .catch(() => {
          this.setParsedTabletLogoUrl('');
        });
    } else {
      this.setParsedTabletLogoUrl('');
    }
  };

  getUnreadMessageCount = () => {
    messagingService
      .getUnreadCount()
      .then((response: any) => {
        appStore.setUnReadMessageCount(response.data);
      })
      .catch()
      .finally(() => {});
  };
}

export const themes = {
  light: {
    primary: '#FF6D00',
    primary_70: '#FF8F43',
    light_orange: '#FF8F43',
    primary_50: '#FFC299',
    primary_20: '#FFE1CC',
    secondary: '#205CAA',
    secondary_70: '#396FAA',
    secondary_50: '#A5B5C4',
    secondary_20: '#D9DBDD',
    accent_blue: '#4bacef',
    accent_blue_70: '#396FAA',
    accent_gray: '#efefef',
    accent_red: '#f24444',
    accent_yellow: '#FFC300',
    accent_green: '#008080',
    dark_grey: '#424242',
    accent_grey: '#6f6f6f',
    light_grey: '#e0e0e0',
    sidebar: '#424242',
    sidebar_text: '#FFFFFF',
    headers: '#396FAA',
    headers_text: '#FFF',
    background: '#f7f7f7',
    light_green: '#12c700'
  },
  alternative: {
    primary: '#205CAA',
    primary_70: '#396FAA',
    primary_50: '#A5B5C4',
    primary_20: '#D9DBDD',
    secondary: '#FF6D00',
    secondary_70: '#FF8F43',
    light_orange: '#FF8F43',
    secondary_50: '#FFC299',
    secondary_20: '#FFE1CC',
    accent_blue: '#4BACEF',
    accent_blue_70: '#396FAA',
    accent_gray: '#EFEFEF',
    accent_red: '#F24444',
    accent_yellow: '#FFC300',
    accent_green: '#008080',
    dark_grey: '#424242',
    accent_grey: '#6f6f6f',
    light_grey: '#e0e0e0',
    sidebar: '#205CAA',
    sidebar_text: '#FFFFFF',
    headers: '#373d44',
    headers_text: '#FFF',
    background: '#f7f7f7',
    light_green: '#12c700'
  }
};

const appStore = new AppStore();

export default appStore;
